<template>
    <div class="modern-nav border-b border-[var(--bone)]">
        <div class="modern-nav-container container flex items-center py-7 relative">
            <!-- Brand -->
            <div class="modern-nav-brand flex items-center">
                <div class="brand-logo mr-3 w-12 h-12 flex items-center justify-center flex-shrink-0"
                    v-if="tourOperatorLogo && tourOperatorLogo.logo">
                    <img :src="tourOperatorLogo.logo" :alt="tourOperatorName"
                        class="max-w-full max-h-full object-contain" />
                </div>
                <div class="brand-icon mr-3 w-12 h-12 rounded-lg bg-gradient-to-br from-teal-600 to-teal-400 flex items-center justify-center text-white"
                    v-else>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M16 4L4 10V22L16 28L28 22V10L16 4Z" fill="currentColor" opacity="0.2" />
                        <path d="M16 4V16M16 16L4 10M16 16L28 10M16 16V28M16 28L4 22M16 28L28 22" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <div class="brand-text flex flex-col">
                    <div class="brand-title text-lg font-bold text-[var(--charcoal-dark)] leading-tight">{{
                        tourOperatorName }}</div>
                    <div class="brand-subtitle text-xs text-gray-500 leading-none">Tours</div>
                </div>
            </div>

            <!-- Progress Steps -->
            <div class="nav-steps flex-1 flex items-center justify-end overflow-x-auto gap-2">
                <div
                    class="nav-steps-inner inline-block border border-[var(--bone)] bg-gradient-to-br from-[#F4ECE1] to-[#F5F3F0] rounded-2xl p-1 shadow-sm max-[1023px]:absolute max-[1023px]:right-3 max-[1023px]:top-[calc(100%-20px)] max-[1023px]:z-50"
                    :class="[mobileMenuOpen ? 'block' : 'hidden', 'lg:block']"
                >
                    <div class="nav-steps-wrap flex flex-col lg:flex-row items-start lg:items-center justify-end gap-2 overflow-x-auto scrollbar-hide">
                        <div 
                            v-for="(step, index) in steps" 
                            :key="index"
                            :data-track="'nav_' + step.title.toLowerCase().replace(/[^a-z0-9]+/g, '_')"
                            class="nav-step flex items-center xl:gap-3 p-2 xl:px-6 xl:py-3 rounded-2xl cursor-pointer transition-all flex-shrink-0 relative"
                            :class="{
                                'active bg-[var(--charcoal)] text-white': tabs === step.id,
                                'completed text-[var(--charcoal)]': tabs > step.id,
                                'disabled opacity-50 cursor-not-allowed': !canNavigateTo(step.id)
                            }" @click="handleNavigation(step.id, step.route)">

                            <div class="step-content flex flex-col items-start">
                                <div class="step-title text-base font-medium leading-tight">{{ step.title }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Hamburger Menu for mobile -->
            <button class="lg:hidden ml-auto flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg" @click="mobileMenuOpen = !mobileMenuOpen">
                <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
            </button>

            <!-- Close Button -->
            <!-- <button v-if="iframeStatus" class="nav-close w-10 h-10 flex items-center justify-center flex-shrink-0" @click="closeIframe" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button> -->
        </div>
    </div>
</template>

<script>
import { flush as flushAnalytics } from '@/analytics/tracker';

export default {
    name: 'NavBtns',
    props: ['comboIds', 'tabs'],
    data() {
        return {
            iframeStatus: true,
            mobileMenuOpen: false
        };
    },
    created() {
        this.iframeStatus = this.$store.state.iframeStatus;
    },
    computed: {
        tourOperatorLogo() {
            return this.$store.state.tourPackageLogo;
        },
        tourOperatorName() {
            return this.tourOperatorLogo && this.tourOperatorLogo.name
                ? this.tourOperatorLogo.name
                : 'Native Journeys';
        },
        hasCustomFields() {
            return this.$store.state.hasCustomFields;
        },
        isComboPackage() {
            return this.$store.state.comboIds && this.$store.state.comboIds !== 0;
        },
        hasCartItems() {
            const cartItem = this.$store.state.cartItem || {};
            return Object.keys(cartItem).length > 0;
        },
        steps() {
            const steps = [];

            // All flows start with Browse and Schedule
            steps.push({ id: 1, title: 'Tours', route: 'Index' });
            steps.push({ id: 2, title: 'Schedule', route: 'Init' });

            if (this.isComboPackage) {
                // Combination package flow
                if (this.hasCustomFields === true) {
                    steps.push({ id: 3, title: 'Add-Ons', route: 'Addons' });
                    steps.push({ id: 4, title: 'My Trip', route: 'MyTrip' });
                    steps.push({ id: 5, title: 'Trip Map', route: 'Maps' });
                    steps.push({ id: 6, title: 'Checkout', route: 'Checkout' });
                } else {
                    // No add-ons for combo
                    steps.push({ id: 3, title: 'My Trip', route: 'MyTrip' });
                    steps.push({ id: 4, title: 'Trip Map', route: 'Maps' });
                    steps.push({ id: 5, title: 'Checkout', route: 'Checkout' });
                }
            } else {
                // Single package flow
                if (this.hasCustomFields === true) {
                    steps.push({ id: 3, title: 'Add-Ons', route: 'Addons' });
                    steps.push({ id: 4, title: 'Checkout', route: 'Checkout' });
                } else {
                    // No add-ons for single
                    steps.push({ id: 3, title: 'Checkout', route: 'Checkout' });
                }
            }

            return steps;
        }
    },
    methods: {
        getStepNumber(stepId) {
            // Return the visual step number (1, 2, 3, 4) based on position in steps array
            const index = this.steps.findIndex(s => s.id === stepId);
            return index + 1;
        },
        canNavigateTo(stepId) {
            // If cart has items, enable all menus
            if (this.hasCartItems) {
                return true;
            }

            // No cart items: control based on package type
            if (this.isComboPackage) {
                // Combo packages: only allow Index (1) when no cart items, or current/previous steps
                return stepId === 1 || stepId <= this.tabs;
            } else {
                // Single package: allow Index (1) and Init (2) by default, or current/previous steps
                return stepId <= 2 || stepId <= this.tabs;
            }
        },
        handleNavigation(tab, destination) {
            if (this.canNavigateTo(tab)) {
                this.$emit('navigatetotab', tab, destination);
            }
        },
        closeIframe() {
            flushAnalytics(); // parent may hide the iframe without unloading it
            window.parent.postMessage({ message: 'close_iframe' }, '*');
        }
    }
};
</script>
